import { ReactElement, ReactNode } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

export type SortableChildrenProps = {
  dragListeners?: SyntheticListenerMap;
  dragAttributes: DraggableAttributes;
  isDragDisabled?: boolean;
  isDragging?: boolean;
};

export type SortableProps = {
  id: string;
  isDragDisabled?: boolean;
  className?: string;
  children: (props: SortableChildrenProps) => ReactElement;
};

export function Sortable({ id, isDragDisabled, className, children }: SortableProps) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: id,
    disabled: isDragDisabled,
  });
  return (
    <div
      ref={setNodeRef}
      className={className}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      {children({
        dragListeners: listeners,
        dragAttributes: attributes,
        isDragDisabled,
        isDragging,
      })}
    </div>
  );
}

export type SortableListProps<T extends { id: string }> = {
  items: T[];
  onMove: (item: T, toItem: T) => void;
  onDraggingChange?: (isDragging: boolean) => void;
  children: ReactNode;
};

export function SortableList<T extends { id: string }>({
  items,
  onMove,
  onDraggingChange,
  children,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    onDraggingChange?.(false);
    const activeItem = items.find((item) => item.id === event.active.id);
    const overItem = items.find((item) => item.id === event.over?.id);
    if (activeItem && overItem && activeItem !== overItem) {
      onMove(activeItem, overItem);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => onDraggingChange?.(true)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => onDraggingChange?.(false)}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}
